/**
 * Chat Service
 * Handles chat interactions with the AI tutor
 */

import { apiClient, extractData, withRetry } from './api';
import type {
  Subject,
  ModerationSeverity,
} from '@kids-lms/types';

export interface SendMessageRequest {
  childId: string;
  content: string;
  conversationId?: string;
  subject?: Subject;
  companion?: string;
  mode?: string;
}

export interface LessonStateResponse {
  isComplete: boolean;
  score: number;
  questionsCorrect: number;
  questionsTotal: number;
}

export interface SendMessageResponse {
  message: {
    id: string;
    conversationId: string;
    role: 'user';
    content: string;
    moderationStatus: string;
    createdAt: string;
  };
  aiResponse: {
    id: string;
    conversationId: string;
    role: 'assistant';
    content: string;
    moderationStatus: string;
    createdAt: string;
  };
  conversationId: string;
  blocked: boolean;
  moderation: {
    inputAllowed: boolean;
    inputSeverity: ModerationSeverity;
    outputSafe: boolean;
    outputSeverity: ModerationSeverity;
  };
  tokenUsage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  lessonState?: LessonStateResponse;
}

export interface ProgressSummary {
  childId: string;
  subjectProgress: Array<{
    subject: string;
    topicCount: number;
    avgScore: number | null;
    highestMastery: string;
    lastActivity: string | null;
  }>;
  totalLessonsCompleted: number;
  totalHomeworkSessions: number;
  weeklyTimeMinutes: number;
  weeklySessionCount: number;
}

export interface ConversationWithMessages {
  conversation: {
    id: string;
    childId: string;
    subject: string | null;
    flagged: boolean;
    platform: string;
    messageCount: number;
    createdAt: string;
    lastMessageAt: string;
  };
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    moderationStatus: string;
    createdAt: string;
  }>;
}

export interface ConversationListItem {
  id: string;
  childId: string;
  subject: string | null;
  flagged: boolean;
  platform: string;
  messageCount: number;
  createdAt: string;
  lastMessageAt: string;
}

export interface ConversationsResponse {
  conversations: ConversationListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Send a message and get AI response
 */
export async function sendMessage(data: SendMessageRequest): Promise<SendMessageResponse> {
  const response = await withRetry(() =>
    apiClient.post<{ success: boolean; data: SendMessageResponse }>('/chat/send', data),
    3 // Max 3 retries
  );
  return extractData(response);
}

/**
 * Get conversation history
 */
export async function getConversation(conversationId: string): Promise<ConversationWithMessages> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ConversationWithMessages }>(`/chat/history/${conversationId}`)
  );
  return extractData(response);
}

/**
 * Get all conversations for a child
 */
export async function getConversations(
  childId: string,
  page: number = 1,
  limit: number = 20
): Promise<ConversationsResponse> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ConversationsResponse }>(
      `/chat/conversations/${childId}`,
      { params: { page, limit } }
    )
  );
  return extractData(response);
}

/**
 * Start a new conversation
 */
export async function startConversation(childId: string, subject?: Subject): Promise<SendMessageResponse> {
  return sendMessage({
    childId,
    content: subject
      ? `Let's learn about ${subject} today!`
      : `Hi! I'm ready to learn something new!`,
    subject,
  });
}

/**
 * Check if content is appropriate before sending (client-side pre-check)
 */
export function preCheckContent(content: string): { valid: boolean; message?: string } {
  // Check length
  if (content.trim().length < 2) {
    return { valid: false, message: 'Message is too short' };
  }
  if (content.length > 1000) {
    return { valid: false, message: 'Message is too long (max 1000 characters)' };
  }

  return { valid: true };
}

/**
 * Get progress summary for a child
 */
export async function getProgressSummary(childId: string): Promise<ProgressSummary> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ProgressSummary }>(`/children/${childId}/progress-summary`)
  );
  return extractData(response);
}

export default {
  sendMessage,
  getConversation,
  getConversations,
  startConversation,
  preCheckContent,
  getProgressSummary,
};
